import { type ReadonlyVec2, vec2 } from 'gl-matrix';

import { lineMidpoint } from '@canvas/math/lines';
import { perpendicularAngle, lerp, reduceAngle } from '@canvas/math/numbers';
import { vec2extended } from '@canvas/math/vec2';

import { type PathSegmentLine, type PathSegment, type PathSegmentArc } from '../types';

import { createLine } from './line';

export function isArcSegment(segment: PathSegment): segment is PathSegmentArc {
  return segment.type === 'arc';
}

export function createArc(
  radius: number,
  startAngle: number,
  endAngle: number,
  counterClockwise?: boolean
): PathSegmentArc {
  return {
    type: 'arc',
    radius,
    startAngle,
    endAngle,
    counterClockwise,
  };
}

/**
 * Calculates a new point along the given arc from a start position and an angle.
 *
 * @param start the starting point
 * @param arc the arc to trace along for finding the new point
 * @param angleDelta the angle between the starting point and the new point
 */
export const getPointAlongArc = (
  start: ReadonlyVec2,
  arc: PathSegmentArc,
  angleDelta: number
) => {
  const center = vec2extended.centerFromArc(
    start,
    arc.startAngle,
    arc.radius,
    arc.counterClockwise
  );

  // Rotation is needed to perform the calculation based on the angle of the _tangent_
  const rotationAngle = arc.counterClockwise ? -Math.PI / 2 : Math.PI / 2;
  const newPoint = vec2extended.fromArcProperties(
    center,
    arc.radius,
    angleDelta,
    rotationAngle
  );

  return newPoint;
};

/**
 * Explodes an arc into a series of lines
 *
 * @param start the start of the arc
 * @param arc the arc to split up
 * @param options.numLines the number of lines to split the arc into
 * @param options.overlap should the algorithm produce an overlapping set of lines for smooth rendering purposes
 *
 * Example of how the arc is divided into discrete line segments based on a
 * given angle delta:
 * {@link https://hello.atlassian.net/wiki/spaces/CAN/whiteboard/852525e6-d963-4b7a-8729-574825a98412}
 */
export const convertArcToLines = (
  start: ReadonlyVec2,
  arc: PathSegmentArc,
  options: { numLines?: number; overlap?: boolean } = {}
) => {
  const { numLines = 5, overlap = false } = options;
  const lines = [];
  let cursor = vec2.clone(start);
  let arcLength = 0;

  // Extra line to fill in the gap before the first constituent line segment
  const angleDelta = (arc.endAngle - arc.startAngle) / numLines;
  const pointBeforeArcStart = getPointAlongArc(start, arc, arc.startAngle - angleDelta);
  let prevMidpoint = lineMidpoint(pointBeforeArcStart, start);
  let prevMidpointArcLength = -Math.abs((arc.radius * angleDelta) / 2);

  // We have an extra iteration, to fill in the gap after the last line segment
  for (let i = 1; i <= numLines + 1; i++) {
    const angle = arc.startAngle + angleDelta * i;
    const newPoint = getPointAlongArc(start, arc, angle);
    const newArcLength = Math.abs(arc.radius * angleDelta * i);

    if (overlap) {
      // Fill in the gaps between line segments by drawing extra lines between the
      // midpoints of each pair of lines
      const currMidpoint = lineMidpoint(cursor, newPoint);
      const currMidpointArcLength = (newArcLength + arcLength) / 2;

      lines.push({
        start: prevMidpoint,
        end: currMidpoint,
        startArcLength: prevMidpointArcLength,
        endArcLength: currMidpointArcLength,
      });

      prevMidpoint = currMidpoint;
      prevMidpointArcLength = currMidpointArcLength;
    }

    if (i <= numLines) {
      lines.push({
        start: cursor,
        end: newPoint,
        startArcLength: arcLength,
        endArcLength: newArcLength,
      });

      cursor = newPoint;
      arcLength = newArcLength;
    }
  }

  return lines;
};

/**
 * Gets the length along an arc
 *
 * @param arc
 * @returns the length as a number
 */

export const getArcLength = (arc: PathSegmentArc) => {
  const angleMagnitude = Math.abs(arc.endAngle - arc.startAngle);
  return angleMagnitude * arc.radius;
};

export type ArcDivisionOptions = {
  /**
   * if provided, this will be the number of divisions created along the arc
   */
  divisions?: number;
  /**
   * the maximum length that each output line segment should be
   * this is used to determine the number of line segments to produce
   */
  maxLineLength?: number;
  /**
   * the maximum angle that each output line segment should be
   * this is used to determine the number of line segments to produce
   */
  maxLineAngle?: number;
};

/**
 * Derives the number of divisions to split an arc into
 * according to the maximum length or angle of each division
 * so you can convert arcs to lines with an appropriate level of detail
 *
 * @param arc arc segment
 * @param options ArcToLinesOptions
 * @returns number of divisions
 */
export const getArcLineDivisions = (
  arc: PathSegmentArc,
  {
    divisions,
    maxLineLength = Number.POSITIVE_INFINITY,
    maxLineAngle = Math.PI,
  }: ArcDivisionOptions
): number => {
  if (divisions) {
    return divisions;
  }

  const totalLength = getArcLength(arc);
  const totalTurn = reduceAngle(arc.endAngle - arc.startAngle);

  return Math.max(
    Math.ceil(totalLength / maxLineLength),
    Math.ceil(Math.abs(totalTurn / maxLineAngle)),
    1
  );
};

/**
 * Breaks down an arc segment into an array of line segments.
 *
 * @param arc the arc segment
 * @param divisions options to influence how many line segments are produced
 */
export const convertArcToPathSegmentLines = (
  arc: PathSegmentArc,
  {
    divisions,
    maxLineLength = Number.POSITIVE_INFINITY,
    maxLineAngle = Math.PI,
  }: ArcDivisionOptions,
  lines: PathSegmentLine[] = []
) => {
  const totalLength = getArcLength(arc);
  const totalTurn = reduceAngle(arc.endAngle - arc.startAngle);

  const finalDivisions =
    divisions ??
    Math.max(
      Math.ceil(totalLength / maxLineLength),
      Math.ceil(Math.abs(totalTurn / maxLineAngle)),
      1
    );

  const divisionLength = totalLength / finalDivisions;
  const divisionTurn = totalTurn / finalDivisions;

  // begin line at half of a turn of a division so that
  // the new line segments follow the curve and end in the same location
  let angle = arc.startAngle + divisionTurn / 2;
  for (let i = 0; i < finalDivisions; i++) {
    lines.push(createLine(angle, divisionLength));
    angle += divisionTurn;
  }
  return lines;
};

/**
 * Given an arc and a point, this returns a proportion
 * describing how far along the arc the angle toward the point is
 * e.g. a proportion of 0 says that the point is the same angle as the start of the arc
 * a proportion of 1 says that the point is the same angle as the end of the arc
 *
 * @param start start of arc
 * @param segment arc segment
 * @param point point to project angle toward
 * @returns proportion
 */
export function getClosestPointAlongArc(
  start: ReadonlyVec2,
  segment: PathSegmentArc,
  point: ReadonlyVec2
): { proportion: number; distance: number } {
  const counterClockwise = !!segment.counterClockwise;

  const v = vec2extended.centerFromArc(
    start,
    segment.startAngle,
    segment.radius,
    segment.counterClockwise
  );
  vec2.sub(v, point, v);
  const angleToPoint = vec2extended.angle(v);
  const distanceToPoint = vec2.len(v);
  const sign = counterClockwise ? -1 : 1;
  const angleFromStart = reduceAngle(
    (angleToPoint - segment.startAngle) * sign + Math.PI * 0.5
  );
  const proportion = angleFromStart / Math.abs(segment.endAngle - segment.startAngle);

  // if proportion is less than 0, get distance to start point
  if (proportion < 0) {
    const vecToStart = vec2.sub(vec2.create(), point, start);
    return { proportion: 0, distance: vec2.len(vecToStart) };
  }

  // if proportion is more than 1, get distance to end point
  if (proportion > 1) {
    const end = getPointOnArc(start, segment, 1);
    const vecToEnd = vec2.sub(vec2.create(), point, end);
    return { proportion: 1, distance: vec2.len(vecToEnd) };
  }

  const distance = Math.abs(distanceToPoint - segment.radius);
  return { proportion, distance };
}

/**
 * Gets the point on an arc, given the arc and a proportion from 0-1
 * where 0 is the start of the arc and 1 is the end
 * returns a vec2
 *
 * @param start
 * @param segments
 * @param proportion
 * @returns
 */
export function getPointOnArc(
  start: ReadonlyVec2,
  segment: PathSegmentArc,
  proportion: number
): vec2 {
  const counterClockwise = !!segment.counterClockwise;

  const centerOfArc = vec2extended.centerFromArc(
    start,
    segment.startAngle,
    segment.radius,
    counterClockwise
  );

  const angleDelta = segment.endAngle - segment.startAngle;
  const angleFromCenterToStart = perpendicularAngle(segment.startAngle, counterClockwise);
  const angleFromCenterToPoint = angleFromCenterToStart + angleDelta * proportion;
  const centerToPoint = vec2extended.fromPolar(angleFromCenterToPoint, segment.radius);

  return vec2.add(vec2.create(), centerOfArc, centerToPoint);
}

/**
 * Gets the tangent angle of a point on an arc, given the arc and a proportion from 0-1
 * where 0 is the start of the arc and 1 is the end
 * returns angle of the tangent of the path at that point
 *
 * @param segments
 * @param proportion
 * @returns
 */

export function getTangentOnArc(segment: PathSegmentArc, proportion: number): number {
  return lerp(segment.startAngle, segment.endAngle, proportion);
}
